import { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  LayoutDashboard, ClipboardList, PlusCircle, FileText, Users, Settings, LogOut, ScrollText, UserCog
} from 'lucide-react';

const PAGE_TITLES = {
  '/dashboard': 'Dashboard',
  '/checklists': 'My Checklists',
  '/checklists/new': 'Start New Run',
  '/admin/templates': 'Templates',
  '/admin/runs': 'All Runs',
  '/admin/users': 'Users',
  '/admin/settings': 'Settings',
  '/admin/audit-log': 'Audit Log',
};

const HIDE_TOPBAR = ['/admin/templates/', '/checklists/'];

function getPageTitle(pathname) {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  return null;
}

function shouldShowTopBar(pathname) {
  if (PAGE_TITLES[pathname]) return true;
  for (const prefix of HIDE_TOPBAR) {
    if (pathname.startsWith(prefix) && pathname !== prefix) return false;
  }
  return false;
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [inProgressCount, setInProgressCount] = useState(0);
  const initials = user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';

  useEffect(() => {
    api.get('/runs?status=in_progress&limit=50').then(r => setInProgressCount(r.data.length)).catch(() => {});
  }, [location.pathname]);

  const isGlobalAdmin = user?.role === 'global_admin';
  const isAdmin = user?.role === 'admin' || isGlobalAdmin;

  const navItems = [
    { section: 'MAIN', items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/checklists', label: 'My checklists', icon: ClipboardList, badge: inProgressCount || null },
      { to: '/checklists/new', label: 'Start new run', icon: PlusCircle },
      { to: '/profile', label: 'Profile', icon: UserCog },
    ]},
    { section: 'ADMIN', adminOnly: true, items: [
      { to: '/admin/templates', label: 'Templates', icon: FileText },
      { to: '/admin/runs', label: 'All runs', icon: ClipboardList },
      { to: '/admin/users', label: 'Users', icon: Users },
      ...(isGlobalAdmin ? [
        { to: '/admin/settings', label: 'Settings', icon: Settings },
        { to: '/admin/audit-log', label: 'Audit log', icon: ScrollText },
      ] : []),
    ]}
  ];

  const pageTitle = getPageTitle(location.pathname);
  const showTopBar = shouldShowTopBar(location.pathname);

  const ROLE_LABELS = { global_admin: 'Global Admin', admin: 'Admin', staff: 'Staff' };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-screen overflow-hidden" data-testid="app-shell">
        <nav className="w-[220px] flex-shrink-0 flex flex-col border-r border-zinc-800" style={{ background: 'var(--brand-hero, #1C3557)' }}>
          <div className="px-4 py-5">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-sm flex items-center justify-center text-xs font-black tracking-tight bg-white/10 border border-white/20">
                <span className="text-white">CB</span>
              </div>
              <div>
                <div className="text-white text-sm font-semibold leading-tight">CheckBuild</div>
                <div className="text-white/40 text-[10px] font-medium leading-tight">Project Checklists</div>
              </div>
            </div>
          </div>
          <Separator className="bg-white/10" />
          <div className="flex-1 overflow-y-auto py-4 px-3">
            {navItems.map(section => {
              if (section.adminOnly && !isAdmin) return null;
              return (
                <div key={section.section} className="mb-4">
                  <div className="px-2 mb-2 text-[10px] font-bold tracking-[0.2em] uppercase text-white/30">
                    {section.section}
                  </div>
                  {section.items.map(item => {
                    const Icon = item.icon;
                    const active = location.pathname === item.to || (item.to !== '/dashboard' && location.pathname.startsWith(item.to));
                    return (
                      <NavLink key={item.to} to={item.to}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`flex items-center gap-2.5 px-2.5 py-2 rounded-sm text-[13px] font-medium mb-0.5 transition-colors duration-150 ${
                          active ? 'bg-white/[0.14] text-white' : 'text-white/60 hover:bg-white/[0.07] hover:text-white/90'
                        }`}
                      >
                        <Icon size={15} strokeWidth={2} />
                        <span className="flex-1">{item.label}</span>
                        {item.badge ? (
                          <Badge variant="secondary" data-testid={`nav-badge-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className="bg-white/20 text-white border-0 text-[10px] font-semibold px-1.5 py-0 h-5 min-w-[20px] justify-center rounded-sm">
                            {item.badge}
                          </Badge>
                        ) : null}
                      </NavLink>
                    );
                  })}
                </div>
              );
            })}
          </div>
          <Separator className="bg-white/10" />
          <div className="px-3 py-3 flex items-center gap-2.5">
            <Avatar className="h-8 w-8 rounded-sm bg-white/15">
              <AvatarFallback className="bg-white/15 text-white text-[11px] font-semibold rounded-sm">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-white text-[12px] font-semibold truncate">{user?.name}</div>
              <div className="text-white/40 text-[10px] font-medium">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={logout} data-testid="logout-btn" className="text-white/40 hover:text-white/80 transition-colors duration-150">
                  <LogOut size={14} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right"><p>Sign out</p></TooltipContent>
            </Tooltip>
          </div>
        </nav>
        <div className="flex-1 flex flex-col overflow-hidden">
          {showTopBar && pageTitle && (
            <header className="h-[52px] bg-white border-b border-zinc-200 px-6 flex items-center justify-between flex-shrink-0" data-testid="top-bar">
              <h1 className="text-[15px] font-bold tracking-tight text-zinc-900" data-testid="page-title">{pageTitle}</h1>
              <div id="top-bar-actions" className="flex items-center gap-2" />
            </header>
          )}
          <main className="flex-1 overflow-y-auto bg-zinc-50">
            <Outlet />
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
